import { Injectable } from '@angular/core';
import { DataService } from '../services/data.service';
import { BlobType } from '../enums/blob-storage/blob-type.enum';
import { BehaviorSubject, from, Observable, Subscription, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AzureBlobStorageService extends DataService {
  private uploadSubscription: Subscription;
  private progressSource = new BehaviorSubject<string>('');
  progress$ = this.progressSource.asObservable();

  constructor() {
    super();
  }

  async upload(file: File, type: BlobType, fileExtension?: string): Promise<{ uriToken: string; blobFileName: string }> {
    const response = await this.getUriToken(file, type, fileExtension);
    await this.http.putBlob(response.UriToken, file, progress => this.progressSource.next(progress)).toPromise();

    return {
      uriToken: response.UriToken,
      blobFileName: response.FileName
    };
  }

  async uploadInChunks(file: File, type: BlobType.INDEXED_DB | BlobType.VIDEO): Promise<string> {
    const response = await this.getUriToken(file, type);
    await this.http.putBlobInChunks(response.UriToken, response.FileName, file, type, progress => this.progressSource.next(progress)).toPromise();

    return response.UriToken;
  }

  uploadWithCancel(file: File, type: BlobType.INDEXED_DB | BlobType.VIDEO): Observable<any> {
    return from(this.getUriToken(file, type)).pipe(
      switchMap(response =>
        this.http.putBlobInChunks(response.UriToken, response.FileName, file, type, progress => {
          this.progressSource.next(progress);
        })
      ),
      catchError(error => {
        console.log(error);
        return throwError(() => new Error(error));
      })
    );
  }

  async cancelUpload(): Promise<void> {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
  }

  private async getUriToken(
    file: File,
    type: BlobType,
    fileExtension?: string
  ): Promise<{
    UriToken: string;
    FileName: string;
  }> {
    switch (type) {
      case BlobType.PHOTO:
        return this.getPhotoUriToken(file);
      case BlobType.VIDEO:
        return this.getVideoUriToken(file);
      case BlobType.INDEXED_DB:
        return this.getIndexedDbUriToken();
      case BlobType.ODOMETER_READING:
        return this.getOdometerReadingUriToken(file, fileExtension);
    }
  }

  private async getPhotoUriToken(file: File): Promise<{
    UriToken: string;
    FileName: string;
  }> {
    const response = await this.http.post<{ Result: { UriToken: string; FileName: string } }>(this.BLOB_STORAGE_ENDPOINTS[BlobType.PHOTO], { FileName: file.name, isThumb: false }).toPromise();
    return response.Result;
  }

  private async getVideoUriToken(file: File): Promise<{
    UriToken: string;
    FileName: string;
  }> {
    const url = this.http.buildUrl(this.BLOB_STORAGE_ENDPOINTS[BlobType.VIDEO].write, { filename: file.name });
    const response = await this.http.get<{ UriToken: string; FileName: string }>(url).toPromise();
    return response.body;
  }

  private async getIndexedDbUriToken(): Promise<{
    UriToken: string;
    FileName: string;
  }> {
    const response = await this.http.get<{ UriToken: string; FileName: string }>(this.BLOB_STORAGE_ENDPOINTS[BlobType.INDEXED_DB].write).toPromise();
    return response.body;
  }

  private async getOdometerReadingUriToken(file: File, fileExtension?: string): Promise<{
    UriToken: string;
    FileName: string;
  }> {

    const response = await this.http.post<{
      fileName: string;
      uriToken: string;
      expiryTime: string;
    }>(this.BLOB_STORAGE_ENDPOINTS[BlobType.ODOMETER_READING], {
      fileExtension: fileExtension
    }).toPromise();
    
    return {
      UriToken: response.uriToken,
      FileName: response.fileName
    };
  }
}
